import { ReactElement, useEffect, useContext } from 'react';
import { useFormikContext, FormikContextType } from 'formik';
import { MtplQuotation } from '../../../../model/quotation';
import { QuotationCardContext } from '../../../../contexts/QuotationCardContext';
import { QuotationContext } from '../../../../contexts/QuotationContext';
import { FormTitle } from '../../../../components/Form/FormTitle';
import { FormRow } from '../../../../components/Form/FormRow';
import { strings } from '../../../../strings/strings';
import { ParametrizedField } from '../../../../components/ParametrizedField';
import { getConstructionYearOptions } from '../../../../model/vehicle';
import { VinInfoMessage } from '../../../../components/InfoMessage/VinInfoMessage';
import { SimpleTooltip } from '../../../../components/SimpleTooltip';
import { addYears } from '../../../../utils/dateUtils';
import { FormItem } from '../../../../components/Form/FormItem';
import { useMediaQuery } from '@material-ui/core';
import { searchCity } from '../../../../api/vehicleApiClient';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { FormItem as FormItemControl } from '../../../../components/inputs/FormItem';
import sign from "../../../../images/sign.svg";
import { ReactSVG } from "react-svg";
import { StepControls } from '../../../../components/Scenario/Step/StepControls';
import { fieldsName } from "./consts";
import FocusError from '../../../../components/FocusError';
import { getAgreeAboutProductLabel, ProductType } from "../../other/agreementCheckbox"
import useSwitchToNewContent from "../../../../utils/hooks/useSwitchToNewContent";

interface FormContext {
    quotation: MtplQuotation
    agree1: boolean
    agree2: boolean
}

interface Props {
    onBack: (formProps: FormikContextType<FormContext>) => void
}

export const Form = ({ onBack }: Props): ReactElement => {
    const formProps = useFormikContext<FormContext>();
    const { setFieldValue, values, handleSubmit } = formProps;

    const { nextIsAvailable } = useContext(QuotationCardContext);
    const {
        setAgreeOption1,
        setAgreeOption2,
        setAgreeInformOption,
        saveTempValues
    } = useContext<QuotationContext<MtplQuotation>>(QuotationContext);

    const isMobile = useMediaQuery('(max-width: 1023px)');

    const showNewContent = useSwitchToNewContent();

    useEffect(() => {
        saveTempValues('quotation', JSON.stringify(values.quotation))
    }, [values?.quotation])

    const convertContextNameFunction = (name: string) =>
        name.replaceAll("-", ".").replaceAll("Party", "")

    const formatResponseForAutocomplete = (response: any) => {
        return {
            name: response.label,
            code: response.value,
        }
    }
    const formatSelectedOptionForAutocomplete = (option: any) => {
        return {
            label: option?.name,
            value: option?.code
        }
    }

    return (
        <form id={"form"} className="personal-info-form form">
            <div className="personal-info-form__sub-block">
                <FormTitle>
                    <h1>{strings.vehicleData}</h1>
                </FormTitle>

                <FormRow className='personal-info-form__sub-block__vehicle-data'>
                    <ParametrizedField
                        contextName={fieldsName.vehicleBrand}
                        convertContextNameFunction={convertContextNameFunction}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext}
                        onChangeListener={(value: string) => {
                            if (value === null) {
                                setFieldValue('quotation.mtplContract.vehicle.model', null)
                            }
                        }}
                        formatResponse={formatResponseForAutocomplete}
                        formatValue={formatSelectedOptionForAutocomplete}
                    />
                    <ParametrizedField
                        contextName={fieldsName.vehicleConstructionYear}
                        convertContextNameFunction={convertContextNameFunction}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext}
                        numericSelectOptions={getConstructionYearOptions}
                    />
                    <ParametrizedField
                        contextName={fieldsName.vehicleModel}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext}
                        contextPath={'mainContract'}
                        formatResponse={formatResponseForAutocomplete}
                        formatValue={formatSelectedOptionForAutocomplete}
                    />
                    <ParametrizedField
                        contextName={fieldsName.vehicleVin}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext}
                        mask={"*****************"}
                        fieldInfoText={strings.whatIsVin}
                        messageInfoObj={<VinInfoMessage />}
                        toUpperCase={true}
                    />
                    <ParametrizedField
                        contextName={fieldsName.vehiclePlateNumber}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext}
                        defaultLabel={strings.vehiclePlateNumber}
                        small={true}
                        plateNumber={true}
                        formatResponse={(option: string) => {
                            return option.replaceAll(" ", "");
                        }}
                    />
                    {showNewContent && (<>
                        <ParametrizedField
                            contextName={fieldsName.engineType}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                        />
                        <ParametrizedField
                            contextName={fieldsName.vehicleBatteryCapacity}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            tooltipLabel={'Якщо в техпаспорті не вказані параметри, встановіть в полі значення 1'}
                        />
                        <ParametrizedField
                            contextName={fieldsName.vehicleEngineCapacity}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            tooltipLabel={'Якщо в техпаспорті не вказані параметри, встановіть в полі значення 1'}
                        />
                        <ParametrizedField
                            contextName={fieldsName.vehicleTotalWeight}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                        />
                        <ParametrizedField
                            contextName={fieldsName.vehicleOwnWeight}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                        />
                        <ParametrizedField
                            contextName={fieldsName.vehicleNumberSeating}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            tooltipLabel={'Якщо в техпаспорті не вказані параметри, встановіть в полі значення 1'}
                        />
                    </>)}
                </FormRow>
            </div>

            <div className="personal-info-form__sub-block smart-tooltip-on-personal-info-form-wrapper">
                <FormTitle>
                    <SimpleTooltip
                        id={'smart-tooltip-on-personal-info-form'}
                        placement={'left'}
                        title={<span>{strings.personalInfoDataSmartTooltipText}</span>}
                    >
                        <h1>{strings.personalInfoData}</h1>
                    </SimpleTooltip>
                </FormTitle>
                <FormRow>
                    <ParametrizedField
                        contextName={fieldsName.primaryInsuredPartySurname}
                        convertContextNameFunction={convertContextNameFunction}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext}
                        additionalFieldInfo={strings.greenDataStep.cyrillic}
                    />
                    <ParametrizedField
                        contextName={fieldsName.primaryInsuredPartyGivenName}
                        convertContextNameFunction={convertContextNameFunction}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext}
                        additionalFieldInfo={strings.greenDataStep.cyrillic}
                    />
                    <ParametrizedField
                        contextName={fieldsName.primaryInsuredPartyMiddleName}
                        convertContextNameFunction={convertContextNameFunction}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext}
                        additionalFieldInfo={strings.greenDataStep.cyrillic}
                    />
                    <ParametrizedField
                        contextName={fieldsName.primaryInsuredPartyBirthDate}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext}
                        convertContextNameFunction={convertContextNameFunction}
                        minDate={addYears(new Date(), -100)}
                        maxDate={addYears(new Date(), -18)}
                    />
                    <ParametrizedField
                        contextName={fieldsName.primaryInsuredPartyIpn}
                        convertContextNameFunction={convertContextNameFunction}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext} mask={"9999999999"}
                    />
                    <ParametrizedField
                        contextName={fieldsName.vehicleOwnerPartyType}
                        convertContextNameFunction={convertContextNameFunction}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext}
                        defaultLabel={strings.partyTypePerson}
                    />
                    <ParametrizedField
                        contextName={fieldsName.primaryInsuredPartyTranslitSurname}
                        convertContextNameFunction={convertContextNameFunction}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext}
                        additionalFieldInfo={strings.greenDataStep.tooltipLatin}
                        defaultLabel={strings.personalInfoSurname}
                    />
                    <ParametrizedField
                        contextName={fieldsName.primaryInsuredPartyTranslitGivenName}
                        convertContextNameFunction={convertContextNameFunction}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext}
                        additionalFieldInfo={strings.greenDataStep.tooltipLatin}
                        defaultLabel={strings.personalInfoName}
                    />
                    <ParametrizedField
                        contextName={fieldsName.primaryInsuredPartyRegistrationDocumentNumber}
                        convertContextNameFunction={convertContextNameFunction}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext}
                        defaultLabel={strings.greenCardDocumentNumber}
                        noSpace={true}
                        toUpperCase={true}
                    />
                    <FormItem colspan={isMobile ? 1 : 3}>
                        <ParametrizedField
                            contextName={fieldsName.primaryInsuredPartyResident}
                            convertContextNameFunction={convertContextNameFunction}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            defaultLabel={strings.personalInfoResident}
                            onChangeListener={(val) => {
                                let residentCountry = val ? {
                                    name: "Україна",
                                    code: "UA"
                                } : null;
                                setFieldValue('quotation.mtplContract.primaryInsured.residentCountry', residentCountry)
                            }}
                        />
                    </FormItem>
                    <ParametrizedField
                        contextName={fieldsName.primaryInsuredPartyResidentCountry}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext}
                        convertContextNameFunction={convertContextNameFunction}
                        formatResponse={formatResponseForAutocomplete}
                        formatValue={formatSelectedOptionForAutocomplete}
                    />
                    <FormItem colspan={isMobile ? 1 : 2}>
                        <ParametrizedField
                            contextName={fieldsName.primaryInsuredPartyLivingAddressTranslitUnstructured}
                            convertContextNameFunction={convertContextNameFunction}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            defaultLabel={strings.personalInfoLivingAddress}
                            additionalFieldInfo={strings.greenDataStep.tooltipLatin}
                        />
                    </FormItem>
                    <ParametrizedField
                        contextName={fieldsName.primaryInsuredPartyEmail}
                        convertContextNameFunction={convertContextNameFunction}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext}
                        placeholder={'example@mail.com'}
                    />
                    <ParametrizedField
                        contextName={fieldsName.primaryInsuredPartyMobileNumber}
                        convertContextNameFunction={convertContextNameFunction}
                        quotationName={'quotation.mtplContract.'}
                        context={QuotationCardContext}
                        mask={"+38(999)999-99-99"}
                    />
                    {!showNewContent &&
                        (
                            <ParametrizedField
                                contextName={fieldsName.agreementPlace}
                                convertContextNameFunction={convertContextNameFunction}
                                quotationName={'quotation.mtplContract.'}
                                context={QuotationCardContext}
                                formatResponse={formatResponseForAutocomplete}
                                formatValue={formatSelectedOptionForAutocomplete}
                                charactersToSearch={3}
                                fieldInfoText={strings.whatIsAgreementPlace}
                                messageInfoObj={<div>{strings.agreementPlaceDescription}</div>}
                                infiniteScroll
                                lazyDefaultOptions={(query: string, size: number) => searchCity(query, size)}
                            />
                        )}
                </FormRow>
            </div>
            <FormRow columns={1}>
                <div className='personal-info-form__agree'>
                    <FormItemControl name={'agreeInform'} validation={{ custom: (value) => !value && strings.validation.required }}>
                        <Checkbox
                            label={getAgreeAboutProductLabel(ProductType.GREEN_CARD)}
                            onChange={setAgreeInformOption}
                        />
                    </FormItemControl>
                </div>
            </FormRow>

            <FormRow columns={1}>
                <div className='personal-info-form__agree'>
                    <FormItemControl name={'agree1'} validation={{ custom: (value) => !value && strings.validation.required }}>
                        <Checkbox
                            label={strings.greenAgreementCheckbox}
                            onChange={setAgreeOption1}
                        />
                    </FormItemControl>
                </div>
            </FormRow>

            <FormRow columns={1}>
                <div className='personal-info-container__GreenAgreeData'>
                    {strings.greenPeronalDataAgreement}
                </div>
            </FormRow>

            <FormRow columns={1}>
                <div className='personal-info-form__agree'>
                    <FormItemControl name={'agree2'} validation={{ custom: (value) => !value && strings.validation.required }}>
                        <Checkbox
                            label={strings.greenFaxSignAgreement}
                            onChange={setAgreeOption2}
                        />
                    </FormItemControl>
                </div>
            </FormRow>

            <FormRow columns={2}>
                <FormRow columns={1}>
                    <span className='personal-info-form__agree-title'>
                        {strings.agreement2}
                    </span>

                    <span className='personal-info-form__agree-value'>
                        {strings.agreement4}
                    </span>
                </FormRow>

                <FormRow columns={1}>
                    <span className='personal-info-form__agree-title'>
                        {strings.agreement3}
                    </span>

                    <span className='personal-info-form__agree-value'>
                        {process.env.REACT_APP_SIGN_VISIBLE === "true" && <ReactSVG src={sign} />}
                    </span>
                </FormRow>
            </FormRow>

            <FocusError />

            <StepControls
                onNext={() => handleSubmit()}
                onBack={() => onBack(formProps)}
                nextIsAvailable={nextIsAvailable}
            />
        </form>
    )
}